import React, { useEffect, useState, Fragment } from "react";
import { Row, Col, Card, Button, Modal, Form, Badge, Spinner, Table, InputGroup } from "react-bootstrap";
import { FaCheck, FaCheckDouble, FaCopy, FaEdit, FaEnvelope, FaEye, FaHistory, FaInfoCircle, FaPaperPlane, FaPlay, FaPlus, FaSearch, FaStop, FaSyncAlt, FaTimes, FaWhatsapp } from "react-icons/fa";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import swal from "sweetalert";

const EMAIL_MODULES = [
  {
    key: "ShopDrawingApproval",
    label: "Shop Drawing Approval",
    timing: "Day 5, Day 7, Day 10",
    trigger: "Shop drawing submitted to client/consultant",
  },
  {
    key: "WorkfrontUnavailable",
    label: "Workfront Not Available",
    timing: "Immediate",
    trigger: "Material delivered but installation cannot start",
  },
  {
    key: "IdleMaterial",
    label: "Idle Material",
    timing: "3-5 days after delivery",
    trigger: "Material remains unused",
  },
  {
    key: "BillingCycle",
    label: "Billing Cycle",
    timing: "Day 0, Day 10, Day 15",
    trigger: "Work milestone completed",
  },
  {
    key: "MaterialShortage",
    label: "Material Shortage",
    timing: "Immediate",
    trigger: "Engineer reports shortage in daily report",
  },
  {
    key: "DailyReportDelay",
    label: "Daily Report Delay",
    timing: "6 PM and next morning",
    trigger: "Engineer does not submit report by 6 PM",
  },
  {
    key: "SiteWorkCompletion",
    label: "Site Work Completion",
    timing: "Immediate",
    trigger: "Work package completed",
  },
  {
    key: "TheftRisk",
    label: "Theft Risk Indicator",
    timing: "Condition based",
    trigger: "Material issue, consumption, missing report, or unused material risk",
  },
];

const DEFAULT_MODULES = EMAIL_MODULES.reduce((acc, item) => {
  acc[item.key] = true;
  return acc;
}, {});

const EmailTemplateMaster = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");
  const [filtered, setFiltered] = useState([]);
  const [importing, setImporting] = useState(false);
  const [automation, setAutomation] = useState(null);
  const [whatsappAutomation, setWhatsappAutomation] = useState(null);
  const [automationSaving, setAutomationSaving] = useState(false);
  const [whatsappSaving, setWhatsappSaving] = useState(false);
  const [whatsappCopyNumbers, setWhatsappCopyNumbers] = useState([]);
  const [whatsappInput, setWhatsappInput] = useState("");
  const [whatsappSavingNumbers, setWhatsappSavingNumbers] = useState(false);
  const [runningAutomation, setRunningAutomation] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showTestRunModal, setShowTestRunModal] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingMail, setSendingMail] = useState(false);

  // WhatsApp Logs & Webhook modal state
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsList, setLogsList] = useState([]);
  const [logsPagination, setLogsPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
  const [logsStats, setLogsStats] = useState({ totalAll: 0, sent: 0, delivered: 0, read: 0, failed: 0 });
  const [logsFilterStatus, setLogsFilterStatus] = useState("all");
  const [logsFilterCopy, setLogsFilterCopy] = useState("all");
  const [logsSearch, setLogsSearch] = useState("");

  const [form, setForm] = useState({
    name: "",
    subject: "",
    body: "",
    type: "Custom",
    status: "Active",
  });
  const [sendForm, setSendForm] = useState({
    templateId: "",
    subject: "",
    body: "",
    date: "",
    time: "",
  });
  // Multiple recipients for To / CC / BCC
  const [toEmails, setToEmails] = useState([]);
  const [ccEmails, setCcEmails] = useState([]);
  const [bccEmails, setBccEmails] = useState([]);
  const [showCcField, setShowCcField] = useState(false);
  const [showBccField, setShowBccField] = useState(false);
  const [toInput, setToInput] = useState("");
  const [ccInput, setCcInput] = useState("");
  const [bccInput, setBccInput] = useState("");
  const [showPreview, setShowPreview] = useState(false);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await api.get("/emails/templates?includeInactive=true");
      if (res.data.success) {
        setList(res.data.data);
        setFiltered(res.data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAutomation = async () => {
    try {
      const res = await api.get("/emails/automation");
      if (res.data.success) {
        setAutomation({
          ...res.data.data,
          modules: { ...DEFAULT_MODULES, ...(res.data.data.modules || {}) },
        });
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchWhatsAppAutomation = async () => {
    try {
      const res = await api.get("/whatsapp/automation");
      if (res.data.success) {
        setWhatsappAutomation(res.data.data);
        setWhatsappCopyNumbers(res.data.data?.copyNumbers || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchWhatsAppLogs = async (page = 1, status = logsFilterStatus, isCopy = logsFilterCopy, search = logsSearch) => {
    setLogsLoading(true);
    try {
      const res = await api.get("/whatsapp/logs", {
        params: {
          page,
          limit: 10,
          status: status !== "all" ? status : undefined,
          isCopy: isCopy !== "all" ? isCopy : undefined,
          search: search.trim() || undefined,
        },
      });
      if (res.data.success) {
        setLogsList(res.data.data || []);
        setLogsPagination(res.data.pagination || { page: 1, limit: 10, total: 0, totalPages: 1 });
        setLogsStats(res.data.stats || { totalAll: 0, sent: 0, delivered: 0, read: 0, failed: 0 });
      }
    } catch (err) {
      console.error("Error fetching WhatsApp logs:", err);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const res = await api.post("/emails/templates/import");
      if (res.data.success) {
        swal("Success", "Templates imported successfully", "success");
        fetchTemplates();
      }
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to import templates";
      swal("Error", msg, "error");
    } finally {
      setImporting(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
    fetchAutomation();
    fetchWhatsAppAutomation();
  }, []);

  useEffect(() => {
    if (!search.trim()) {
      setFiltered(list);
    } else {
      const s = search.toLowerCase();
      setFiltered(
        list.filter(
          (i) =>
            i.name.toLowerCase().includes(s) ||
            i.subject.toLowerCase().includes(s)
        )
      );
    }
  }, [search, list]);

  const openAdd = () => {
    setEditId(null);
    setForm({
      name: "",
      subject: "",
      body: "",
      type: "Custom",
      status: "Active",
    });
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditId(item._id);
    setForm({
      name: item.name,
      subject: item.subject,
      body: item.body,
      type: item.type || "Custom",
      status: item.status,
    });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = editId
        ? await api.put(`/emails/templates/${editId}`, form)
        : await api.post("/emails/templates", form);

      if (res.data.success) {
        swal("Success", res.data.message, "success");
        setShowModal(false);
        fetchTemplates();
      }
    } catch (err) {
      swal("Error", err.response?.data?.message || "Something went wrong", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleAutomationToggle = async () => {
    if (!automation) return;
    setAutomationSaving(true);
    try {
      const res = await api.patch("/emails/automation", {
        enabled: !automation.enabled,
      });
      if (res.data.success) {
        setAutomation({
          ...res.data.data,
          modules: { ...DEFAULT_MODULES, ...(res.data.data.modules || {}) },
        });
        swal("Success", res.data.message, "success");
      }
    } catch (err) {
      swal("Error", err.response?.data?.message || "Failed to update automatic emails", "error");
    } finally {
      setAutomationSaving(false);
    }
  };

  const handleWhatsAppToggle = async () => {
    if (!whatsappAutomation) return;

    setWhatsappSaving(true);
    try {
      const res = await api.patch("/whatsapp/automation", {
        enabled: !whatsappAutomation.enabled,
      });
      if (res.data.success) {
        setWhatsappAutomation(res.data.data);
        if (res.data.data?.copyNumbers) {
          setWhatsappCopyNumbers(res.data.data.copyNumbers);
        }
        swal("Success", res.data.message, "success");
      }
    } catch (err) {
      swal("Error", err.response?.data?.message || "Failed to update WhatsApp notifications", "error");
    } finally {
      setWhatsappSaving(false);
    }
  };

  const handleAddWhatsappNumber = () => {
    const raw = String(whatsappInput || "").replace(/[^0-9]/g, "");
    if (!raw || raw.length < 10) {
      swal("Invalid Mobile Number", "Please enter a valid 10-digit mobile number", "warning");
      return;
    }
    const clean = raw.length === 10 ? "91" + raw : raw;
    if (whatsappCopyNumbers.includes(clean)) {
      swal("Already Added", "This number is already in the copy list", "info");
      return;
    }
    setWhatsappCopyNumbers([...whatsappCopyNumbers, clean]);
    setWhatsappInput("");
  };

  const handleRemoveWhatsappNumber = (numToRemove) => {
    setWhatsappCopyNumbers((prev) => prev.filter((n) => n !== numToRemove));
  };

  const handleSaveWhatsappNumbers = async () => {
    setWhatsappSavingNumbers(true);
    try {
      const res = await api.patch("/whatsapp/automation", {
        copyNumbers: whatsappCopyNumbers,
      });
      if (res.data.success) {
        setWhatsappAutomation(res.data.data);
        setWhatsappCopyNumbers(res.data.data?.copyNumbers || []);
        swal("Success", "WhatsApp notification copy numbers saved successfully", "success");
      }
    } catch (err) {
      swal("Error", err.response?.data?.message || "Failed to save WhatsApp copy numbers", "error");
    } finally {
      setWhatsappSavingNumbers(false);
    }
  };

  const handleModuleToggle = async (moduleKey) => {
    if (!automation) return;

    const nextModules = {
      ...DEFAULT_MODULES,
      ...(automation.modules || {}),
      [moduleKey]: !(automation.modules || DEFAULT_MODULES)[moduleKey],
    };

    setAutomationSaving(true);
    try {
      const res = await api.patch("/emails/automation", {
        modules: nextModules,
      });
      if (res.data.success) {
        setAutomation({
          ...res.data.data,
          modules: { ...DEFAULT_MODULES, ...(res.data.data.modules || {}) },
        });
        swal("Success", res.data.message, "success");
      }
    } catch (err) {
      swal("Error", err.response?.data?.message || "Failed to update module email setting", "error");
    } finally {
      setAutomationSaving(false);
    }
  };

  const handleAllModules = async (enabled) => {
    if (!automation) return;

    const modules = EMAIL_MODULES.reduce((acc, item) => {
      acc[item.key] = enabled;
      return acc;
    }, {});

    setAutomationSaving(true);
    try {
      const res = await api.patch("/emails/automation", { modules });
      if (res.data.success) {
        setAutomation({
          ...res.data.data,
          modules: { ...DEFAULT_MODULES, ...(res.data.data.modules || {}) },
        });
        swal("Success", res.data.message, "success");
      }
    } catch (err) {
      swal("Error", err.response?.data?.message || "Failed to update module email settings", "error");
    } finally {
      setAutomationSaving(false);
    }
  };

  const handleRunAutomation = async (e) => {
    e.preventDefault();
    setRunningAutomation(true);
    try {
      const res = await api.post("/emails/automation/run", { testEmail });
      if (res.data.success) {
        swal("Success", res.data.message, "success");
        setShowTestRunModal(false);
        setTestEmail("");
        fetchAutomation();
      }
    } catch (err) {
      swal("Error", err.response?.data?.message || "Failed to run automatic email check", "error");
    } finally {
      setRunningAutomation(false);
    }
  };

  const openSendModal = (template = null) => {
    setSendForm({
      templateId: template?._id || "",
      subject: template?.subject || "",
      body: template?.body || "",
      date: "",
      time: "",
    });
    setToEmails([]);
    setCcEmails([]);
    setBccEmails([]);
    setShowCcField(false);
    setShowBccField(false);
    setToInput("");
    setCcInput("");
    setBccInput("");
    setShowSendModal(true);
  };

  const handleTemplateSelect = (e) => {
    const templateId = e.target.value;
    const selected = list.find((item) => item._id === templateId);
    setSendForm((prev) => ({
      ...prev,
      templateId,
      subject: selected?.subject || "",
      body: selected?.body || "",
    }));
  };

  const handleSendChange = (e) => {
    const { name, value } = e.target;
    setSendForm((prev) => ({ ...prev, [name]: value }));
  };

  // helpers for multi-email tag inputs
  const addEmailTo = (list, setter, input, setInput) => {
    const val = input.trim();
    if (val && !list.includes(val)) setter([...list, val]);
    setInput("");
  };
  const removeEmailFrom = (setter, email) => setter((prev) => prev.filter((e) => e !== email));

  const handleManualSend = async (e) => {
    e.preventDefault();
    if (toEmails.length === 0) {
      swal("Error", "Please add at least one recipient in To field", "error");
      return;
    }
    setSendingMail(true);
    try {
      const res = await api.post("/emails/send", {
        to: toEmails,
        cc: ccEmails.length > 0 ? ccEmails : undefined,
        bcc: bccEmails.length > 0 ? bccEmails : undefined,
        subject: sendForm.subject,
        body: sendForm.body,
        date: sendForm.date,
        time: sendForm.time,
      });
      if (res.data.success) {
        swal("Success", "Email sent successfully", "success");
        setShowSendModal(false);
      }
    } catch (err) {
      swal("Error", err.response?.data?.message || "Failed to send email", "error");
    } finally {
      setSendingMail(false);
    }
  };

  const moduleSettings = { ...DEFAULT_MODULES, ...(automation?.modules || {}) };
  const activeModuleCount = EMAIL_MODULES.filter((item) => moduleSettings[item.key]).length;
  const automationEnabled = Boolean(automation?.enabled);
  const whatsappEnabled = Boolean(whatsappAutomation?.enabled);

  return (
    <Fragment>
      <PageTitle activeMenu="Notifications" motherMenu="Masters" />
      <Row className="mb-4">
        {/* Gmail Control Center */}
        <Col xl={6} lg={12} className="mb-3 mb-xl-0">
          <Card className="h-100 shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center flex-wrap bg-white pb-3">
              <div>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center" style={{ width: 32, height: 32 }}>
                    <FaEnvelope size={14} />
                  </div>
                  <Card.Title className="mb-0 fs-5">Gmail Control Center</Card.Title>
                </div>
                <Badge bg={automationEnabled ? "success" : "danger"}>
                  {automationEnabled ? "Gmail Notifications Running" : "Gmail Notifications Stopped"}
                </Badge>
              </div>
              <div className="d-flex flex-wrap gap-2 mt-2 mt-sm-0">
                <Button
                  variant={automationEnabled ? "outline-danger" : "outline-success"}
                  size="sm"
                  onClick={handleAutomationToggle}
                  disabled={!automation || automationSaving}
                >
                  {automationSaving ? (
                    "Updating..."
                  ) : automationEnabled ? (
                    <>
                      <FaStop className="me-1" /> Stop Gmail
                    </>
                  ) : (
                    <>
                      <FaPlay className="me-1" /> Start Gmail
                    </>
                  )}
                </Button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setShowTestRunModal(true)}
                  disabled={runningAutomation}
                >
                  <FaSyncAlt className="me-1" />
                  {runningAutomation ? "Running..." : "Test"}
                </Button>
                <Button variant="primary" size="sm" onClick={() => openSendModal()}>
                  <FaPaperPlane className="me-1" /> Compose
                </Button>
              </div>
            </Card.Header>

            <Card.Body className="d-flex flex-column justify-content-between">
              <Row className="g-2 mb-3">
                <Col sm={4}>
                  <div className="border rounded p-2 text-center bg-light h-100">
                    <div className="text-muted small mb-1" style={{ fontSize: "11px" }}>Last Run</div>
                    <strong className="d-block text-dark small" style={{ fontSize: "12px" }}>
                      {automation?.lastRunAt ? new Date(automation.lastRunAt).toLocaleDateString() : "Not run yet"}
                    </strong>
                    <small className="text-muted" style={{ fontSize: "10.5px" }}>
                      {automation?.lastRunAt ? new Date(automation.lastRunAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                    </small>
                  </div>
                </Col>
                <Col sm={4}>
                  <div className="border rounded p-2 text-center bg-light h-100">
                    <div className="text-muted small mb-1" style={{ fontSize: "11px" }}>Auto Schedule</div>
                    <strong className="d-block text-dark small" style={{ fontSize: "12px" }}>Runs daily</strong>
                    <small className="text-muted" style={{ fontSize: "10.5px" }}>10:00 AM IST</small>
                  </div>
                </Col>
                <Col sm={4}>
                  <div className="border rounded p-2 text-center bg-light h-100">
                    <div className="text-muted small mb-1" style={{ fontSize: "11px" }}>Modules Active</div>
                    <strong className="d-block text-success small" style={{ fontSize: "13px" }}>
                      {activeModuleCount} / {EMAIL_MODULES.length}
                    </strong>
                    <small className="text-muted" style={{ fontSize: "10.5px" }}>Enabled</small>
                  </div>
                </Col>
              </Row>

              <div className="p-3 bg-light rounded border">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <div>
                    <strong className="d-block text-dark small">Client Notification Rules</strong>
                    <span className="text-muted small" style={{ fontSize: "11.5px" }}>
                      Configure individual automated client email triggers across the 8 modules below.
                    </span>
                  </div>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleImport}
                    disabled={importing}
                  >
                    <FaSyncAlt className="me-1" />
                    {importing ? "Importing..." : "Sync Templates"}
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* WhatsApp Control Center */}
        <Col xl={6} lg={12}>
          <Card className="h-100 shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center flex-wrap bg-white pb-3">
              <div>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <div className="rounded-circle bg-success text-white d-flex align-items-center justify-content-center" style={{ width: 32, height: 32 }}>
                    <FaWhatsapp size={16} />
                  </div>
                  <Card.Title className="mb-0 fs-5">WhatsApp Control Center</Card.Title>
                </div>
                <Badge bg={whatsappEnabled ? "success" : "danger"}>
                  {whatsappEnabled ? "WhatsApp Notifications Running" : "WhatsApp Notifications Stopped"}
                </Badge>
              </div>
              <div className="d-flex gap-2 mt-2 mt-sm-0">
                <Button
                  variant={whatsappEnabled ? "outline-danger" : "outline-success"}
                  size="sm"
                  onClick={handleWhatsAppToggle}
                  disabled={!whatsappAutomation || whatsappSaving}
                >
                  {whatsappSaving ? (
                    "Updating..."
                  ) : whatsappEnabled ? (
                    <>
                      <FaStop className="me-1" /> Stop WhatsApp
                    </>
                  ) : (
                    <>
                      <FaPlay className="me-1" /> Start WhatsApp
                    </>
                  )}
                </Button>
                <Button
                  variant="outline-info"
                  size="sm"
                  onClick={() => {
                    setShowLogsModal(true);
                    fetchWhatsAppLogs(1, logsFilterStatus, logsFilterCopy, logsSearch);
                  }}
                >
                  <FaHistory className="me-1" /> View Logs
                </Button>
              </div>
            </Card.Header>

            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                  <strong className="small text-dark">Notification Copy Recipients (CC)</strong>
                  <div className="text-muted small" style={{ fontSize: "11px" }}>
                    A copy of tasks, DPR updates, and daily approval digests will be delivered to these numbers.
                  </div>
                </div>
                <Badge bg="primary" className="px-2 py-1">
                  {whatsappCopyNumbers.length} {whatsappCopyNumbers.length === 1 ? "Number" : "Numbers"}
                </Badge>
              </div>

              <div className="d-flex gap-2 mb-2">
                <InputGroup size="sm">
                  <InputGroup.Text className="bg-light text-muted">+91</InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Enter 10-digit mobile number"
                    value={whatsappInput}
                    onChange={(e) => setWhatsappInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddWhatsappNumber();
                      }
                    }}
                  />
                </InputGroup>
                <Button
                  variant="primary"
                  size="sm"
                  type="button"
                  onClick={handleAddWhatsappNumber}
                  style={{ whiteSpace: "nowrap" }}
                >
                  <FaPlus className="me-1" /> Add
                </Button>
              </div>

              <div
                className="border rounded p-2 mb-2 bg-light"
                style={{ minHeight: "56px", maxHeight: "80px", overflowY: "auto" }}
              >
                {whatsappCopyNumbers.length > 0 ? (
                  <div className="d-flex flex-wrap gap-1">
                    {whatsappCopyNumbers.map((num) => (
                      <Badge
                        key={num}
                        bg="white"
                        text="dark"
                        className="border d-flex align-items-center py-1 px-2 shadow-sm"
                        style={{ fontSize: "12px", fontWeight: "500" }}
                      >
                        <span className="me-2 text-success">+{num}</span>
                        <FaTimes
                          className="text-danger cursor-pointer"
                          style={{ cursor: "pointer" }}
                          title="Remove number"
                          onClick={() => handleRemoveWhatsappNumber(num)}
                        />
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <div className="text-muted small text-center py-1" style={{ fontSize: "11.5px" }}>
                    No copy numbers added yet. Enter a mobile number and click "Add".
                  </div>
                )}
              </div>

              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 pt-2 border-top">
                <span className="text-muted" style={{ fontSize: "11px" }}>
                  <FaInfoCircle className="me-1 text-primary" />
                  <strong>Webhook:</strong> <code>/api/whatsapp/webhook</code>
                </span>
                <Button
                  variant="success"
                  size="sm"
                  onClick={handleSaveWhatsappNumbers}
                  disabled={whatsappSavingNumbers}
                >
                  {whatsappSavingNumbers ? "Saving..." : "Save Copy Numbers"}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-4">
        <Col lg={12}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center flex-wrap">
              <div>
                <Card.Title className="mb-1">Client Email Modules</Card.Title>
                <span className="text-muted small">Rules and timings from notification sheet</span>
              </div>
              <div className="mt-2 mt-sm-0">
                <Button
                  variant="outline-success"
                  size="sm"
                  className="me-2"
                  onClick={() => handleAllModules(true)}
                  disabled={!automation || automationSaving}
                >
                  All On
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleAllModules(false)}
                  disabled={!automation || automationSaving}
                >
                  All Off
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              <Row className="g-3">
                {EMAIL_MODULES.map((item) => {
                  const isEnabled = Boolean(moduleSettings[item.key]);
                  return (
                    <Col xl={3} md={4} sm={6} key={item.key}>
                      <div className={`border rounded p-3 h-100 ${isEnabled ? "bg-light" : ""}`}>
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <strong className="d-block mb-1">{item.label}</strong>
                            <div className="text-muted small mb-2">{item.timing}</div>
                            <Badge bg={isEnabled ? "success" : "secondary"}>
                              {isEnabled ? "On" : "Off"}
                            </Badge>
                            <div className="text-muted small mt-2">{item.trigger}</div>
                          </div>
                          <Form.Check
                            type="switch"
                            id={`email-module-${item.key}`}
                            checked={isEnabled}
                            disabled={!automation || automationSaving}
                            onChange={() => handleModuleToggle(item.key)}
                          />
                        </div>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={12}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center flex-wrap">
              <Card.Title className="mb-2 mb-sm-0">Email Templates</Card.Title>
              <div className="d-flex flex-wrap">
                <Button variant="outline-primary" className="me-2 mb-2 mb-sm-0" onClick={handleImport} disabled={importing}>
                  <FaSyncAlt className="me-1" />
                  {importing ? "Importing..." : "Import Defaults"}
                </Button>
                <Button variant="primary" onClick={openAdd}>
                  <FaPlus className="me-1" /> Add Template
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              <InputGroup className="mb-3">
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search by name or subject..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </InputGroup>

              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : (
                <div className="table-responsive">
                  <Table responsive hover className="align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Name</th>
                        <th>Type</th>
                        <th>Subject</th>
                        <th>Status</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-center py-5 text-muted">No templates found</td>
                        </tr>
                      ) : (
                        filtered.map((item) => (
                          <tr key={item._id}>
                            <td><strong>{item.name}</strong></td>
                            <td>
                              <Badge bg="info">{item.type || "Custom"}</Badge>
                            </td>
                            <td>{item.subject}</td>
                            <td>
                              <Badge bg={item.status === "Active" ? "success" : "secondary"}>
                                {item.status}
                              </Badge>
                            </td>
                            <td className="text-end">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="me-2"
                                onClick={() => openSendModal(item)}
                              >
                                <FaPaperPlane className="me-1" /> Send
                              </Button>
                              <Button
                                variant="outline-warning"
                                size="sm"
                                onClick={() => openEdit(item)}
                              >
                                <FaEdit className="me-1" /> Edit
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{editId ? "Edit" : "Add"} Email Template</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Template Name</Form.Label>
                  <Form.Control
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="e.g. Work Order Approval"
                    required
                  />
                  <Form.Text className="text-muted">
                    Unique identifier for this template.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Template Type</Form.Label>
                  <Form.Select name="type" value={form.type} onChange={handleChange}>
                    <option value="Custom">Custom</option>
                    <option value="Approval">Approval</option>
                    <option value="Reminder">Reminder</option>
                    <option value="Payment">Payment</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select name="status" value={form.status} onChange={handleChange}>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Email Subject</Form.Label>
                  <Form.Control
                    name="subject"
                    value={form.subject}
                    onChange={handleChange}
                    placeholder="Enter email subject"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label className="d-flex justify-content-between">
                    Email Body (HTML supported)
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 text-decoration-none"
                      onClick={() => setShowPreview(!showPreview)}
                    >
                      {showPreview ? "Show Editor" : "Show Preview"}
                    </Button>
                  </Form.Label>
                  {showPreview ? (
                    <div
                      className="border rounded p-3 bg-light"
                      style={{ minHeight: "200px", maxHeight: "400px", overflowY: "auto" }}
                      dangerouslySetInnerHTML={{ __html: form.body }}
                    />
                  ) : (
                    <Form.Control
                      as="textarea"
                      rows={10}
                      name="body"
                      value={form.body}
                      onChange={handleChange}
                      placeholder="Write your email content here..."
                      required
                    />
                  )}
                  <Form.Text className="text-muted">
                    You can use HTML tags like &lt;b&gt;, &lt;br&gt;, &lt;p&gt; etc.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? "Saving..." : "Save Template"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showSendModal} onHide={() => setShowSendModal(false)} size="lg">
        <Form onSubmit={handleManualSend}>
          <Modal.Header closeButton>
            <Modal.Title>Send Manual Email</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Template</Form.Label>
                  <Form.Select value={sendForm.templateId} onChange={handleTemplateSelect}>
                    <option value="">Custom Email</option>
                    {list.map((item) => (
                      <option key={item._id} value={item._id}>
                        {item.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              {/* TO field with tags + CC/BCC toggles */}
              <Col md={12}>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <Form.Label className="mb-0 fw-semibold">To <span className="text-danger">*</span></Form.Label>
                  <div className="d-flex gap-2">
                    {!showCcField && (
                      <Button variant="link" size="sm" className="p-0 text-decoration-none" onClick={() => setShowCcField(true)}>
                        + CC
                      </Button>
                    )}
                    {!showBccField && (
                      <Button variant="link" size="sm" className="p-0 text-decoration-none" onClick={() => setShowBccField(true)}>
                        + BCC
                      </Button>
                    )}
                  </div>
                </div>
                {toEmails.length > 0 && (
                  <div className="d-flex flex-wrap gap-1 mb-2">
                    {toEmails.map((email, idx) => (
                      <Badge key={idx} bg="primary" className="d-flex align-items-center gap-1 px-2 py-1" style={{ fontSize: "0.8rem", fontWeight: 400 }}>
                        {email}
                        <FaTimes style={{ cursor: "pointer", fontSize: "0.7rem" }} onClick={() => removeEmailFrom(setToEmails, email)} />
                      </Badge>
                    ))}
                  </div>
                )}
                <div className="d-flex gap-2 mb-3">
                  <Form.Control
                    type="email"
                    value={toInput}
                    onChange={(e) => setToInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addEmailTo(toEmails, setToEmails, toInput, setToInput); } }}
                    placeholder="Type email & press Enter to add multiple"
                  />
                  <Button variant="outline-secondary" onClick={() => addEmailTo(toEmails, setToEmails, toInput, setToInput)}>
                    <FaPlus />
                  </Button>
                </div>
                <Form.Text className="text-muted d-block" style={{ marginTop: "-0.75rem", marginBottom: "0.5rem" }}>
                  Press Enter or comma to add multiple recipients.
                </Form.Text>
              </Col>

              {/* CC field */}
              {showCcField && (
                <Col md={12}>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <Form.Label className="mb-0 fw-semibold">CC</Form.Label>
                    <Button variant="link" size="sm" className="p-0 text-danger text-decoration-none" onClick={() => { setShowCcField(false); setCcEmails([]); setCcInput(""); }}>
                      Remove CC
                    </Button>
                  </div>
                  {ccEmails.length > 0 && (
                    <div className="d-flex flex-wrap gap-1 mb-2">
                      {ccEmails.map((email, idx) => (
                        <Badge key={idx} bg="info" className="d-flex align-items-center gap-1 px-2 py-1" style={{ fontSize: "0.8rem", fontWeight: 400 }}>
                          {email}
                          <FaTimes style={{ cursor: "pointer", fontSize: "0.7rem" }} onClick={() => removeEmailFrom(setCcEmails, email)} />
                        </Badge>
                      ))}
                    </div>
                  )}
                  <div className="d-flex gap-2 mb-3">
                    <Form.Control
                      type="email"
                      value={ccInput}
                      onChange={(e) => setCcInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addEmailTo(ccEmails, setCcEmails, ccInput, setCcInput); } }}
                      placeholder="Add CC recipients"
                    />
                    <Button variant="outline-secondary" onClick={() => addEmailTo(ccEmails, setCcEmails, ccInput, setCcInput)}>
                      <FaPlus />
                    </Button>
                  </div>
                </Col>
              )}

              {/* BCC field */}
              {showBccField && (
                <Col md={12}>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <Form.Label className="mb-0 fw-semibold">BCC</Form.Label>
                    <Button variant="link" size="sm" className="p-0 text-danger text-decoration-none" onClick={() => { setShowBccField(false); setBccEmails([]); setBccInput(""); }}>
                      Remove BCC
                    </Button>
                  </div>
                  {bccEmails.length > 0 && (
                    <div className="d-flex flex-wrap gap-1 mb-2">
                      {bccEmails.map((email, idx) => (
                        <Badge key={idx} bg="secondary" className="d-flex align-items-center gap-1 px-2 py-1" style={{ fontSize: "0.8rem", fontWeight: 400 }}>
                          {email}
                          <FaTimes style={{ cursor: "pointer", fontSize: "0.7rem" }} onClick={() => removeEmailFrom(setBccEmails, email)} />
                        </Badge>
                      ))}
                    </div>
                  )}
                  <div className="d-flex gap-2 mb-3">
                    <Form.Control
                      type="email"
                      value={bccInput}
                      onChange={(e) => setBccInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addEmailTo(bccEmails, setBccEmails, bccInput, setBccInput); } }}
                      placeholder="Add BCC recipients"
                    />
                    <Button variant="outline-secondary" onClick={() => addEmailTo(bccEmails, setBccEmails, bccInput, setBccInput)}>
                      <FaPlus />
                    </Button>
                  </div>
                </Col>
              )}

              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Subject</Form.Label>
                  <Form.Control
                    name="subject"
                    value={sendForm.subject}
                    onChange={handleSendChange}
                    placeholder="Email subject"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Date</Form.Label>
                  <Form.Control
                    name="date"
                    type="date"
                    value={sendForm.date}
                    onChange={handleSendChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Time</Form.Label>
                  <Form.Control
                    name="time"
                    type="time"
                    value={sendForm.time}
                    onChange={handleSendChange}
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Email Body</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={10}
                    name="body"
                    value={sendForm.body}
                    onChange={handleSendChange}
                    placeholder="Write the email body..."
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowSendModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={sendingMail}>
              {sendingMail ? "Sending..." : "Send Email"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showTestRunModal} onHide={() => setShowTestRunModal(false)}>
        <Form onSubmit={handleRunAutomation}>
          <Modal.Header closeButton>
            <Modal.Title>Test Automatic Check</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Test Email</Form.Label>
              <Form.Control
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="your-test-email@example.com"
                required
              />
              <Form.Text className="text-muted">
                Test emails will go only to this address. Client emails are not used.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowTestRunModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={runningAutomation}>
              {runningAutomation ? "Checking..." : "Run Test Check"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* WhatsApp Delivery Logs & Analytics Modal */}
      <Modal show={showLogsModal} onHide={() => setShowLogsModal(false)} size="xl">
        <Modal.Header closeButton>
          <div className="d-flex align-items-center gap-2">
            <div className="rounded-circle bg-success text-white d-flex align-items-center justify-content-center" style={{ width: 28, height: 28 }}>
              <FaWhatsapp size={15} />
            </div>
            <div>
              <Modal.Title className="fs-5 mb-0">WhatsApp Delivery & Analytics Logs</Modal.Title>
              <small className="text-muted">Real-time status updates from WhatsApp Business API</small>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          {/* Metrics Row */}
          <Row className="g-2 mb-3">
            <Col sm={3} xs={6}>
              <div className="border rounded p-2 text-center bg-light">
                <small className="text-muted d-block" style={{ fontSize: "11px" }}>Total Sent</small>
                <strong className="fs-5 text-dark">{logsStats.totalAll}</strong>
              </div>
            </Col>
            <Col sm={3} xs={6}>
              <div className="border rounded p-2 text-center bg-light">
                <small className="text-muted d-block" style={{ fontSize: "11px" }}>Delivered</small>
                <strong className="fs-5 text-info">{logsStats.delivered}</strong>
              </div>
            </Col>
            <Col sm={3} xs={6}>
              <div className="border rounded p-2 text-center bg-light">
                <small className="text-muted d-block" style={{ fontSize: "11px" }}>Read (Blue Tick)</small>
                <strong className="fs-5 text-success">{logsStats.read}</strong>
              </div>
            </Col>
            <Col sm={3} xs={6}>
              <div className="border rounded p-2 text-center bg-light">
                <small className="text-muted d-block" style={{ fontSize: "11px" }}>Failed</small>
                <strong className="fs-5 text-danger">{logsStats.failed}</strong>
              </div>
            </Col>
          </Row>

          {/* Filter Row */}
          <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
            <div className="flex-grow-1" style={{ minWidth: 200 }}>
              <InputGroup size="sm">
                <InputGroup.Text><FaSearch /></InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search phone or template..."
                  value={logsSearch}
                  onChange={(e) => setLogsSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      fetchWhatsAppLogs(1, logsFilterStatus, logsFilterCopy, e.target.value);
                    }
                  }}
                />
              </InputGroup>
            </div>
            <div>
              <Form.Select
                size="sm"
                value={logsFilterStatus}
                onChange={(e) => {
                  const val = e.target.value;
                  setLogsFilterStatus(val);
                  fetchWhatsAppLogs(1, val, logsFilterCopy, logsSearch);
                }}
              >
                <option value="all">All Statuses</option>
                <option value="sent">Sent</option>
                <option value="delivered">Delivered</option>
                <option value="read">Read (Blue Tick)</option>
                <option value="failed">Failed</option>
              </Form.Select>
            </div>
            <div>
              <Form.Select
                size="sm"
                value={logsFilterCopy}
                onChange={(e) => {
                  const val = e.target.value;
                  setLogsFilterCopy(val);
                  fetchWhatsAppLogs(1, logsFilterStatus, val, logsSearch);
                }}
              >
                <option value="all">All Recipients</option>
                <option value="false">Primary Only</option>
                <option value="true">CC Copy Only</option>
              </Form.Select>
            </div>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => fetchWhatsAppLogs(1, logsFilterStatus, logsFilterCopy, logsSearch)}
              disabled={logsLoading}
            >
              <FaSyncAlt className={logsLoading ? "fa-spin" : ""} />
            </Button>
          </div>

          {/* Logs Table */}
          {logsLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="success" />
              <div className="text-muted small mt-2">Loading WhatsApp logs...</div>
            </div>
          ) : logsList.length === 0 ? (
            <div className="text-center py-5 text-muted border rounded bg-light">
              <FaWhatsapp size={36} className="mb-2 text-muted" />
              <div>No WhatsApp notification logs found.</div>
              <small>Messages sent by the system will appear here automatically.</small>
            </div>
          ) : (
            <div className="table-responsive border rounded">
              <Table hover className="align-middle mb-0" style={{ fontSize: "12.5px" }}>
                <thead className="table-light">
                  <tr>
                    <th>Date & Time</th>
                    <th>Recipient Phone</th>
                    <th>Type</th>
                    <th>Template</th>
                    <th>Status</th>
                    <th>Delivery Analytics</th>
                  </tr>
                </thead>
                <tbody>
                  {logsList.map((item) => (
                    <tr key={item._id}>
                      <td>
                        <div className="text-dark fw-500">
                          {new Date(item.sentAt || item.createdAt).toLocaleDateString()}
                        </div>
                        <small className="text-muted">
                          {new Date(item.sentAt || item.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </small>
                      </td>
                      <td>
                        <strong className="text-dark">+{item.phone}</strong>
                      </td>
                      <td>
                        {item.isCopy ? (
                          <Badge bg="light" text="dark" className="border">CC Copy</Badge>
                        ) : (
                          <Badge bg="primary">Primary</Badge>
                        )}
                      </td>
                      <td>
                        <code className="text-dark">{item.templateName}</code>
                      </td>
                      <td>
                        {item.status === "read" ? (
                          <Badge bg="success" className="d-inline-flex align-items-center">
                            <FaCheckDouble className="me-1" /> Read
                          </Badge>
                        ) : item.status === "delivered" ? (
                          <Badge bg="info" className="d-inline-flex align-items-center">
                            <FaCheckDouble className="me-1" /> Delivered
                          </Badge>
                        ) : item.status === "failed" ? (
                          <Badge bg="danger" className="d-inline-flex align-items-center" title={item.failureReason}>
                            <FaTimes className="me-1" /> Failed
                          </Badge>
                        ) : (
                          <Badge bg="secondary" className="d-inline-flex align-items-center">
                            <FaCheck className="me-1" /> Sent
                          </Badge>
                        )}
                      </td>
                      <td>
                        {item.readAt ? (
                          <div className="text-success small">
                            <FaEye className="me-1" /> Read at {new Date(item.readAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </div>
                        ) : item.deliveredAt ? (
                          <div className="text-info small">
                            Delivered at {new Date(item.deliveredAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </div>
                        ) : item.failureReason ? (
                          <div className="text-danger small" title={item.failureReason} style={{ maxWidth: 200, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {item.failureReason}
                          </div>
                        ) : (
                          <span className="text-muted small">Awaiting status</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          {/* Pagination Controls */}
          {logsPagination.totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3 pt-2">
              <small className="text-muted">
                Page {logsPagination.page} of {logsPagination.totalPages} ({logsPagination.total} total logs)
              </small>
              <div className="d-flex gap-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={logsPagination.page <= 1 || logsLoading}
                  onClick={() => fetchWhatsAppLogs(logsPagination.page - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  disabled={logsPagination.page >= logsPagination.totalPages || logsLoading}
                  onClick={() => fetchWhatsAppLogs(logsPagination.page + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between align-items-center bg-light">
          <div className="small text-muted">
            <FaInfoCircle className="me-1 text-primary" />
            Webhook URL: <code>https://test.canscorp.com/api/whatsapp/webhook</code>
          </div>
          <Button variant="secondary" size="sm" onClick={() => setShowLogsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};

export default EmailTemplateMaster;
// End of EmailTemplateMaster
